import React, { useEffect, useState } from "react";
import { 
    Box, Button, Grid, Paper, Typography, 
    Switch, Chip, Divider
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
    faPlus, faTicketAlt, faStar, faChartLine, 
    faArrowRight, faQrcode 
} from "@fortawesome/free-solid-svg-icons";
import CreatePromotionForm from "./CreatePromotionForm";
import { getPromotions } from "../../../controllers/company/loyalty/PromotionsController";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";


// =====================================================================
// 1. MINI-COMPONENTE: KPI CARD
// =====================================================================
const KpiCard = ({ title, value, icon, iconColor, iconBg }) => (
    <Paper 
        elevation={0} 
        sx={{ 
            p: 2.5, 
            borderRadius: 2, 
            border: '1px solid #eaeaea', 
            bgcolor: '#fff',
            display: 'flex',
            alignItems: 'center',
            height: '100%'
        }}
    >
        <Box 
            sx={{ 
                width: 48, 
                height: 48, 
                borderRadius: 2, 
                bgcolor: iconBg, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                mr: 2 
            }}
        >
            <FontAwesomeIcon icon={icon} size="lg" color={iconColor} />
        </Box>
        <Box>
             <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {title}
            </Typography>
            <Typography variant="h5" sx={{ color: '#1a1a1a', fontWeight: 'bold', mt: 0.2 }}>
                {value}
            </Typography>
        </Box>
    </Paper>
);

// =====================================================================
// 2. DASHBOARD PRINCIPAL
// =====================================================================
export default function MerchantLoyaltyDashboard() {
    
    const [promotions, setPromotions] = useState([]);
    const [kpis, setKpis] = useState();
    const [isModalOpen, setIsModalOpen] = useState(false);

    const history = useHistory();

    useEffect(() => {
        handleGetPromotions();
    }, []);

    const redirect = (route) => {
        history.push(route);
    }

    const handleGetPromotions = async () => {
        const { promotions, stats } = await getPromotions();
        setPromotions(promotions);
        setKpis(stats);
    }

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: 'transparent' }}>

            <CreatePromotionForm open={isModalOpen} onClose={() => setIsModalOpen(false)} />
            
            {/* HEADER */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box>
                    <Typography variant="h5" sx={{ color: '#1a1a1a', fontWeight: 'bold', mb: 0.5 }}>
                        Loyalty Hub
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Monitorea el impacto real de tus campañas y el retorno de inversión.
                    </Typography>
                </Box>
                
                {/* BOTONERA PRINCIPAL */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button 
                        variant="outlined" 
                        startIcon={<FontAwesomeIcon icon={faQrcode} />}
                        onClick={() => history.push('/merchant/loyalty/scanner')} 
                        sx={{ 
                            borderRadius: 1.5, 
                            color: '#1a1a1a',
                            borderColor: '#1a1a1a',
                            textTransform: 'none', 
                            fontWeight: 'bold',
                            px: 3,
                        }} 
                    >
                        Escanear QR
                    </Button>

                    <Button 
                        variant="contained" 
                        disableElevation
                        startIcon={<FontAwesomeIcon icon={faPlus} />} 
                        onClick={() => setIsModalOpen(true)}
                        sx={{ 
                            borderRadius: 1.5, 
                            bgcolor: '#1a1a1a', 
                            textTransform: 'none', 
                            fontWeight: 'bold',
                            px: 3,
                            '&:hover': { bgcolor: '#000' }
                        }} 
                    >
                        Nueva Promoción
                    </Button>
                </Box>
            </Box>

            {/* GRILLA DE KPIs */}
            {
                kpis &&
                <Grid container spacing={3} sx={{ mb: 5 }}>
                    <Grid item xs={12} sm={6} md={6} lg={3}>
                        <KpiCard title="Ventas Generadas" value={`$${kpis.revenue}`} icon={faChartLine} iconColor="#2e7d32" iconBg="#e8f5e9" />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6} lg={3}>
                        <KpiCard title="Canjes Totales" value={kpis.total_redemptions} icon={faQrcode} iconColor="#1565c0" iconBg="#e3f2fd" />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6} lg={3}>
                        <KpiCard title="Clientes QUD Pass" value={kpis.qud_pass_clients} icon={faStar} iconColor="#f57f17" iconBg="#fff8e1" />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6} lg={3}>
                        <KpiCard title="Campañas Activas" value={kpis.active_promos} icon={faTicketAlt} iconColor="#424242" iconBg="#f5f5f5" />
                    </Grid>
                </Grid>
            }
            
            {/* LISTADO DE CAMPAÑAS - DISEÑO HORIZONTAL */}
            <Box>
                <Typography variant="h6" sx={{ color: '#1a1a1a', fontWeight: 'bold', mb: 3 }}>
                    Listado de Promociones
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {promotions.map((promo) => {
                        const isVip = promo.modality === 'qud_pass_exclusive';
                        const isActive = promo.status === 'active';
                        const progress = promo.claimed > 0 ? Math.round((promo.redeemed / promo.claimed) * 100) : 0;
                        
                        return (
                            <Paper 
                                key={promo.id}
                                elevation={0} 
                                sx={{ 
                                    p: { xs: 2, md: 3 }, 
                                    borderRadius: 3,
                                    border: '1px solid #f0f0f0',
                                    bgcolor: '#ffffff',
                                    opacity: isActive ? 1 : 0.6,
                                    display: 'flex',
                                    flexDirection: { xs: 'column', md: 'row' }, // Responsivo: col en móvil, row en PC
                                    justifyContent: 'space-between',
                                    alignItems: { xs: 'flex-start', md: 'center' },
                                    gap: 3,
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                        boxShadow: '0px 8px 24px rgba(0,0,0,0.04)',
                                    }
                                }}
                            >
                                {/* LADO IZQUIERDO: Chip y Título */}
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1 }}>
                                    <Chip 
                                        label={isVip ? 'QUD PASS' : 'PÚBLICO'} 
                                        size="small" 
                                        sx={{ 
                                            fontWeight: 800, 
                                            fontSize: '0.65rem', 
                                            letterSpacing: 0.5,
                                            height: 24,
                                            width: 'fit-content',
                                            bgcolor: isVip ? '#fff8e1' : '#e3f2fd', 
                                            color: isVip ? '#f57f17' : '#1565c0',
                                            borderRadius: 1 // Bordes ligeramente redondeados como en tu imagen
                                        }} 
                                    />
                                    <Typography variant="h6" sx={{ color: '#1a1a1a', fontWeight:'bold', lineHeight: 1.2 }}>
                                        {promo.title}
                                    </Typography>
                                </Box>

                                {/* LADO DERECHO: Estadísticas, Botón y Switch */}
                                <Box sx={{ 
                                    display: 'flex', 
                                    flexDirection: 'row', 
                                    alignItems: 'center', 
                                    gap: { xs: 2, md: 4 },
                                    width: { xs: '100%', md: 'auto' },
                                    justifyContent: { xs: 'space-between', md: 'flex-end' }
                                }}>
                                    
                                    {/* BLOQUE DE MÉTRICAS */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                        <Box>
                                            <Typography variant="caption" sx={{ color: '#a0aab4', fontWeight: 800, display: 'block', mb: 0.5, letterSpacing: 0.5 }}>
                                                RECLAMADOS
                                            </Typography>
                                            <Typography variant="h5" sx={{ fontWeight: 900, color: '#1a1a1a', lineHeight: 1 }}>
                                                {promo.claimed}
                                                <Typography component="span" sx={{ fontSize: '0.85rem', color: '#a0aab4', fontWeight: 600, ml: 0.5 }}>
                                                    / {promo.stock > 0 ? promo.stock : '∞'}
                                                </Typography>
                                            </Typography>
                                        </Box>

                                        <Divider orientation="vertical" flexItem sx={{ borderColor: '#f0f0f0', my: 1 }} />

                                        <Box>
                                            <Typography variant="caption" sx={{ color: '#a0aab4', fontWeight: 800, display: 'block', mb: 0.5, letterSpacing: 0.5 }}>
                                                CANJEADOS
                                            </Typography>
                                            <Typography variant="h5" sx={{ fontWeight: 900, color: '#1a1a1a', lineHeight: 1 }}>
                                                {promo.redeemed}
                                                <Typography component="span" sx={{ fontSize: '0.85rem', color: '#a0aab4', fontWeight: 600, ml: 0.5 }}>
                                                    ({progress}%)
                                                </Typography>
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* BLOQUE DE ACCIÓN (Botón + Switch arriba) */}
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                                        <Switch 
                                            size="small" 
                                            checked={isActive} 
                                            color="default" 
                                            sx={{ 
                                                mr: -1, // Ajuste sutil para alinear con el borde derecho del botón
                                                '& .MuiSwitch-switchBase.Mui-checked': { color: '#000' },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#000' }
                                            }} 
                                        />
                                        <Button 
                                            disableElevation
                                            endIcon={<FontAwesomeIcon icon={faArrowRight} />}
                                            sx={{ 
                                                borderRadius: 2,
                                                textTransform: 'none', 
                                                fontWeight: 800, 
                                                color: '#1a1a1a',
                                                bgcolor: '#f8f9fa',
                                                py: 1,
                                                px: 2,
                                                whiteSpace: 'nowrap', // Evita que el texto se rompa
                                                '&:hover': { bgcolor: '#e9ecef' }
                                            }}
                                            onClick={() => redirect(`/merchant/loyalty/details/${promo.id}`)}
                                        >
                                            Ver estadísticas
                                        </Button>
                                    </Box>

                                </Box>
                            </Paper>
                        );
                    })}
                </Box>
            </Box>
        </Box>
    );
}